import { useReducedMotion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const ERASE_MS = 45;
const TYPE_MS = 75;
const GAP_MS = 220;

type Props = {
  word: string;
  className?: string;
};

// Erases the current text and types `word` whenever it changes.
// Inherits font and color from the parent; pass className (styled(TypingWord)) to override.
const TypingWord = ({ word, className }: Props) => {
  const [text, setText] = useState(word);
  const [isTyping, setIsTyping] = useState(false);
  const textRef = useRef(word);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const show = (value: string) => {
      textRef.current = value;
      setText(value);
    };

    if (reduceMotion) {
      show(word);

      return undefined;
    }

    const type = () => {
      if (textRef.current.length < word.length) {
        show(word.slice(0, textRef.current.length + 1));
        timer = setTimeout(type, TYPE_MS);

        return;
      }

      setIsTyping(false);
    };

    const erase = () => {
      if (textRef.current.length) {
        show(textRef.current.slice(0, -1));
        timer = setTimeout(erase, ERASE_MS);

        return;
      }

      timer = setTimeout(type, GAP_MS);
    };

    if (textRef.current !== word) {
      setIsTyping(true);
      erase();
    }

    return () => clearTimeout(timer);
  }, [word, reduceMotion]);

  return (
    <Container className={className}>
      {text}
      <Caret $visible={isTyping} />
    </Container>
  );
};

// Global styles set font-size/color on every span, so inherit explicitly.
const Container = styled.span`
  font: inherit;
  color: inherit;
  letter-spacing: inherit;
  position: relative;
  display: inline-block;

  /* Keeps the line height (and the caret) while the word is fully erased. */
  height: 1.2em;
  height: 1lh;
  vertical-align: top;
`;

// Inherits so currentcolor and the em offsets follow the word, not the global span style.
const Caret = styled.span<{ $visible: boolean }>`
  font: inherit;
  color: inherit;
  position: absolute;
  top: 0.14em;
  right: -6px;
  bottom: 0.16em;
  width: 3px;
  border-radius: 2px;
  background: currentcolor;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: ${({ $visible }) =>
    $visible ? 'none' : 'opacity 0.4s ease 0.9s'};
`;

export default TypingWord;
